import { useCameras } from '@hakimo-ui/hakimo/data-access';
import { Camera } from '@hakimo-ui/hakimo/types';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { getSearchParams } from '../../../cameras-list/util';
import { DEFAULT_CAM_FILTER } from '../../../constants';
import {
  MultiCamTab,
  committedCameraMapAtom,
  gridOrderAtom,
  removedFromGroupAtom,
} from '../../state';
import { MAX_SELECTED_CAMERAS } from '../../util';

interface TabCameras {
  cameras: Camera[];
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
}

export function useTabCameras(tab: MultiCamTab): TabCameras {
  const committedCameraMap = useAtomValue(committedCameraMapAtom);
  const removedFromGroup = useAtomValue(removedFromGroupAtom);

  const isGroup = tab.type === 'group';
  const cameraGroupId = tab.type === 'group' ? tab.cameraGroupId : undefined;
  const groupSearchParams = useMemo(
    () =>
      cameraGroupId
        ? getSearchParams(DEFAULT_CAM_FILTER, {
            cameraGroupIds: [cameraGroupId],
          })
        : '',
    [cameraGroupId]
  );

  const { data, isFetching, isError, error } = useCameras(
    groupSearchParams,
    isGroup
  );
  const gridOrder = useAtomValue(gridOrderAtom);

  const cameras = useMemo(() => {
    const base =
      tab.type === 'cameras'
        ? Object.values(committedCameraMap)
        : (data?.items || []).filter((cam) => {
            const removed = removedFromGroup[tab.id] || [];
            return !removed.includes(cam.id);
          });
    const order = gridOrder[tab.id];
    let ordered = base;
    if (order && order.length > 0) {
      const rank = new Map(order.map((id, index) => [id, index]));
      ordered = [...base].sort(
        (a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity)
      );
    }
    return tab.type === 'group'
      ? ordered.slice(0, MAX_SELECTED_CAMERAS)
      : ordered;
  }, [tab, committedCameraMap, data?.items, removedFromGroup, gridOrder]);

  return {
    cameras,
    isFetching: isGroup ? isFetching : false,
    isError: isGroup ? isError : false,
    error: isGroup ? error : null,
  };
}

export default useTabCameras;
